/**
 * Composes the world-class CFO forecast Excel workbook as a SpreadsheetWorkbookSnapshot.
 *
 * Phase 1 sheets: Cover & Dashboard, Drivers (with the live driver model),
 * Income Statement / Balance Sheet / Cash Flow (monthly, authoritative ML numbers
 * with a live High/Expected/Low scenario toggle), Scenario Comparison, and
 * Methodology (disclosures). Charts (Phase 2) and weekly/daily cash (Phase 3) are
 * layered on later through the same builder.
 *
 * Two interactive layers, both recalculating natively in Excel:
 *  - the scenario dropdown drives `CHOOSE(Scenario_Index, high, expected, low)`
 *    across every statement cell (exact ML bands), and
 *  - the six editable drivers drive a transparent summary model whose outputs
 *    feed the KPI tiles.
 */
import type {
  SpreadsheetWorkbookSnapshot,
} from '../../../protocol/spreadsheetEngineProtocol'
import {
  SheetWriter,
  WorkbookBuilder,
  crossRangeRef,
  crossRef,
} from './cfoForecastWorkbookBuilder'
import {
  buildCfoWorkbookStyleRegistry,
  CFO_FONTS,
  CFO_PALETTE,
  type CfoWorkbookStyleRegistry,
} from './cfoForecastWorkbookStyles'
import {
  CFO_CASE_LABELS,
  CFO_CASE_ORDER,
  CFO_DRIVER_DEFS,
  seasonalIndexForMonthOffset,
  type CfoCase,
  type CfoDriverData,
  type CfoDriverModelSeed,
} from './cfoForecastDriverModel'
import { checkThreeStatementIntegrity } from './cfoForecastIntegrity'
import { forecastAccuracySummary, forecastAccuracyStatementLabel } from './forecastAccuracy'
import type { QuickBooksFpaMlForecastVsActualRow } from '../../../data/ServerApiClient'

export type CfoStatementType = 'profit_and_loss' | 'balance_sheet' | 'cash_flow'

export type CfoStatementRowData = {
  nodeId: string
  /** Parent node id from the FPA layout tree — preserves the exact online hierarchy. */
  parentNodeId?: string | null
  label: string
  level: number
  rowType: string
  isTotal: boolean
  isFormula: boolean
  /** values per period, per case (aligned to `periods`). */
  byCase: Record<CfoCase, Array<number | null>>
}

export type CfoStatementData = {
  statementType: CfoStatementType
  label: string
  periods: string[]
  periodKinds: Array<'actual' | 'forecast'>
  rows: CfoStatementRowData[]
}

export type CfoForecastWorkbookInput = {
  companyName: string
  anchorPeriod: string | null
  grain: string
  grainLabel: string
  currencySymbol?: string
  generatedAtIso?: string
  version?: string
  /** Monthly statements (P&L / Balance Sheet / Cash Flow), whichever are available. */
  statements: CfoStatementData[]
  /** Optional weekly cash-flow statement for the 13-week liquidity view. */
  weeklyCash?: CfoStatementData | null
  /** Optional daily cash-flow statement for the daily liquidity ledger. */
  dailyCash?: CfoStatementData | null
  drivers: CfoDriverData[]
  seed: CfoDriverModelSeed
  warnings?: string[]
  /** Backtested forecast-vs-actual rows for the Accuracy sheet's historical block. */
  backtest?: CfoForecastBacktest | null
  /** Data vintage + model version + readiness, for the Cover/Methodology honesty layer. */
  vintage?: CfoForecastVintage | null
}

export type CfoForecastBacktest = {
  rows: QuickBooksFpaMlForecastVsActualRow[]
  readinessStatus?: string | null
  basisLabels?: string[] | null
  recentMonths?: number
}

export type CfoForecastVintage = {
  /** QB sync / last closed actual month the forecast was built from. */
  dataAsOf?: string | null
  anchorMonth?: string | null
  /** Model/package version the client can quote back to support. */
  packageId?: string | null
  readinessStatus?: string | null
  /** Months of closed actual history feeding the package. */
  closedMonthsCount?: number | null
}

export type CfoForecastWorkbookResult = {
  snapshot: SpreadsheetWorkbookSnapshot
}

const SHEET = {
  cover: { id: 'cfo_cover', name: 'Cover' },
  drivers: { id: 'cfo_drivers', name: 'Drivers' },
  income: { id: 'cfo_income', name: 'Income Statement' },
  balance: { id: 'cfo_balance', name: 'Balance Sheet' },
  cash: { id: 'cfo_cash', name: 'Cash Flow' },
  scenario: { id: 'cfo_scenario', name: 'Scenario Comparison' },
  range: { id: 'cfo_range', name: 'Forecast Range' },
  weekly: { id: 'cfo_weekly', name: '13-Week Cash' },
  daily: { id: 'cfo_daily', name: 'Daily Cash' },
  accuracy: { id: 'cfo_accuracy', name: 'Accuracy & Confidence' },
  methodology: { id: 'cfo_methodology', name: 'Methodology' },
} as const

const STATEMENT_SHEET: Record<CfoStatementType, { id: string; name: string }> = {
  profit_and_loss: SHEET.income,
  balance_sheet: SHEET.balance,
  cash_flow: SHEET.cash,
}

const LABEL_COL = 0
const FIRST_PERIOD_COL = 1

function fmtMonthLabel(period: string): string {
  // period is an ISO date (YYYY-MM-DD); produce "Mon YYYY".
  const m = /^(\d{4})-(\d{2})/.exec(String(period ?? ''))
  if (!m) return String(period ?? '')
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  const monthIndex = Math.max(0, Math.min(11, Number(m[2]) - 1))
  return `${months[monthIndex]} ${m[1]}`
}

function num(value: number | null | undefined): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null
}

/** `CHOOSE(Scenario_Index, high, expected, low)` with case values inlined. */
function caseChooseFormula(high: number, expected: number, low: number): string {
  return `CHOOSE(Scenario_Index,${high},${expected},${low})`
}

// ---------------------------------------------------------------------------
// Driver model math (mirrors the Excel formulas so cached values are correct)
// ---------------------------------------------------------------------------

type DriverModelProjection = {
  months: number
  revenue: number[]
  cogs: number[]
  grossProfit: number[]
  opex: number[]
  ebitda: number[]
  ar: number[]
  ap: number[]
  cashFromOps: number[]
  endingCash: number[]
}

/**
 * Resolve a driver's value for a scenario, oriented so the High scenario is
 * always cash-*optimistic* and the Low scenario cash-*pessimistic*. For
 * `cashFavorable: 'down'` drivers (DSO, opex) the High scenario picks the driver's
 * LOW statistical value (e.g. faster collections) and vice-versa. `neutral`
 * drivers (seasonality) stay at expected. This keeps "High = more cash" across
 * the whole workbook. Mirrors `cashFavorableChooseFormula` used on the Drivers
 * sheet so cached values match the live formulas exactly.
 */
function activeDriver(drivers: CfoDriverData[], key: string, scenario: CfoCase): number {
  const d = drivers.find((x) => x.key === key)
  if (!d) return 0
  if (scenario === 'expected' || d.cashFavorable === 'neutral') return d.expected
  const optimistic = scenario === 'high'
  const useUpperBand = d.cashFavorable === 'up' ? optimistic : !optimistic
  return useUpperBand ? d.high : d.low
}

/**
 * Orient a Low/Expected/High triple by *value* so Low ≤ High (Expected kept as
 * the ML anchor). Used for the "more is better" cash/revenue/EBITDA/runway
 * metrics on the Scenario, Forecast Range and Accuracy sheets, where the ML's
 * raw band can arrive inverted for sign-flipped aggregates (the favourable
 * more-cash outcome is labelled `low` by the server). Without this the High
 * column can show the *least* cash and the fan-chart bands collapse to zero.
 */
function orientBand(low: number, expected: number, high: number): { low: number; expected: number; high: number } {
  return { low: Math.min(low, high), expected, high: Math.max(low, high) }
}

export function projectDriverModel(
  drivers: CfoDriverData[],
  seed: CfoDriverModelSeed,
  scenario: CfoCase,
): DriverModelProjection {
  const months = Math.max(1, Math.floor(seed.horizonMonths))
  const growth = activeDriver(drivers, 'sales_growth_pct', scenario)
  const margin = activeDriver(drivers, 'gross_margin_pct', scenario)
  const dso = activeDriver(drivers, 'dso_days', scenario)
  const dpo = activeDriver(drivers, 'dpo_days', scenario)
  const opexPct = activeDriver(drivers, 'opex_pct', scenario)
  const seasonality = activeDriver(drivers, 'seasonality_strength', scenario)
  const base = seed.baseMonthlyRevenue

  const revenue: number[] = []
  const cogs: number[] = []
  const grossProfit: number[] = []
  const opex: number[] = []
  const ebitda: number[] = []
  const ar: number[] = []
  const ap: number[] = []
  const cashFromOps: number[] = []
  const endingCash: number[] = []
  const ar0 = base * dso / 30
  const ap0 = base * (1 - margin) * dpo / 30
  for (let t = 1; t <= months; t += 1) {
    const seas = seasonalIndexForMonthOffset(t - 1)
    const rev = base * (1 + growth / 12) ** t * (1 + seasonality * seas)
    const cg = rev * (1 - margin)
    const gp = rev - cg
    const ox = rev * opexPct
    const eb = gp - ox
    const arT = rev * dso / 30
    const apT = cg * dpo / 30
    const prevAr = t === 1 ? ar0 : ar[t - 2]
    const prevAp = t === 1 ? ap0 : ap[t - 2]
    const cfo = eb - (arT - prevAr) + (apT - prevAp)
    const prevCash = t === 1 ? seed.openingCash : endingCash[t - 2]
    revenue.push(rev)
    cogs.push(cg)
    grossProfit.push(gp)
    opex.push(ox)
    ebitda.push(eb)
    ar.push(arT)
    ap.push(apT)
    cashFromOps.push(cfo)
    endingCash.push(prevCash + cfo)
  }
  return { months, revenue, cogs, grossProfit, opex, ebitda, ar, ap, cashFromOps, endingCash }
}

// ---------------------------------------------------------------------------
// Statement sheet (monthly + quarter/year roll-ups, collapsible, scenario-toggled)
// ---------------------------------------------------------------------------

/**
 * How a row aggregates across a quarter/year. Flows (P&L lines, net cash change)
 * SUM; balances (every Balance Sheet line, beginning/ending cash) take the
 * end-of-period value — you must never sum cash balances.
 */
function rowAggregation(statementType: CfoStatementType, label: string): 'sum' | 'first' | 'last' {
  if (statementType === 'balance_sheet') return 'last'
  if (statementType === 'cash_flow') {
    if (/beginning|opening/i.test(label)) return 'first'
    if (/ending|closing|balance/i.test(label)) return 'last'
  }
  return 'sum'
}

type StatementColumn = {
  kind: 'month' | 'quarter' | 'year'
  col: number
  label: string
  forecast: boolean
  /** Underlying month period indices (for cached roll-up math). */
  periodIdxs: number[]
  /** Underlying month column positions (for first/last balance roll-ups). */
  monthCols: number[]
  /** Columns to SUM for flow roll-ups (quarter→months, year→quarters). */
  sumCols: number[]
  outline: number
}

/** Build the month→quarter→year column plan (summaries to the right of their detail). */
function planStatementColumns(
  periods: string[],
  periodKinds: Array<'actual' | 'forecast'>,
  startCol: number,
): { columns: StatementColumn[]; lastCol: number } {
  const columns: StatementColumn[] = []
  const years: string[] = []
  const byYear = new Map<string, number[]>()
  periods.forEach((p, i) => {
    const y = String(p).slice(0, 4)
    if (!byYear.has(y)) { byYear.set(y, []); years.push(y) }
    byYear.get(y)!.push(i)
  })
  let col = startCol
  const majorityForecast = (idxs: number[]) => idxs.filter((i) => periodKinds[i] === 'forecast').length * 2 >= idxs.length
  for (const y of years) {
    const yearIdxs = byYear.get(y)!
    const yearMonthCols: number[] = []
    const quarterCols: number[] = []
    const byQ = new Map<number, number[]>()
    const qOrder: number[] = []
    for (const i of yearIdxs) {
      const q = Math.floor((Number(String(periods[i]).slice(5, 7)) - 1) / 3) + 1
      if (!byQ.has(q)) { byQ.set(q, []); qOrder.push(q) }
      byQ.get(q)!.push(i)
    }
    for (const q of qOrder) {
      const qIdxs = byQ.get(q)!
      const qMonthCols: number[] = []
      for (const i of qIdxs) {
        const c = col++
        columns.push({ kind: 'month', col: c, label: fmtMonthLabel(periods[i]), forecast: periodKinds[i] === 'forecast', periodIdxs: [i], monthCols: [c], sumCols: [c], outline: 2 })
        qMonthCols.push(c); yearMonthCols.push(c)
      }
      const qc = col++
      columns.push({ kind: 'quarter', col: qc, label: `Q${q} ${y}`, forecast: majorityForecast(qIdxs), periodIdxs: qIdxs, monthCols: qMonthCols, sumCols: qMonthCols, outline: 1 })
      quarterCols.push(qc)
    }
    const yc = col++
    columns.push({ kind: 'year', col: yc, label: `FY ${y}`, forecast: majorityForecast(yearIdxs), periodIdxs: yearIdxs, monthCols: yearMonthCols, sumCols: quarterCols, outline: 0 })
  }
  return { columns, lastCol: col - 1 }
}

/**
 * Shared sheet furniture: a thin petrol cap rule, a brass brand kicker (company),
 * a serif title and a muted subtitle — the institutional "letterhead" that makes
 * every tab read like one document. Content should start at row 5 (row 4 spacer).
 */
function cfoSheetHeader(
  sheet: SheetWriter,
  styles: CfoWorkbookStyleRegistry,
  input: CfoForecastWorkbookInput,
  opts: { title: string; subtitle: string; lastCol: number },
): void {
  const last = Math.max(opts.lastCol, 4)
  sheet.put(0, LABEL_COL, { value: '', styleId: styles.custom({ backgroundColor: CFO_PALETTE.brand }) })
  sheet.merge(0, LABEL_COL, 0, last)
  sheet.rowHeight(0, 4)
  sheet.text(1, LABEL_COL, input.companyName.toUpperCase(), styles.id('brandBand'))
  sheet.merge(1, LABEL_COL, 1, last)
  sheet.text(2, LABEL_COL, opts.title, styles.id('coverTitle'))
  sheet.merge(2, LABEL_COL, 2, last)
  sheet.rowHeight(2, 30)
  sheet.text(3, LABEL_COL, opts.subtitle, styles.id('coverMeta'))
  sheet.merge(3, LABEL_COL, 3, last)
}

function buildStatementSheet(
  wb: WorkbookBuilder,
  styles: CfoWorkbookStyleRegistry,
  statement: CfoStatementData,
  input: CfoForecastWorkbookInput,
): SheetWriter {
  const sheetMeta = STATEMENT_SHEET[statement.statementType]
  const sheet = new SheetWriter(sheetMeta.id, sheetMeta.name)
  const periods = statement.periods
  const { columns, lastCol } = planStatementColumns(periods, statement.periodKinds, FIRST_PERIOD_COL)

  cfoSheetHeader(sheet, styles, input, {
    title: statement.label,
    subtitle: `${input.grainLabel} · ${input.currencySymbol ?? '$'} · scenario set on the Drivers sheet · opens at the yearly roll-up — use the +/- controls to expand to quarters, months and account detail`,
    lastCol,
  })

  // Period header + kind + units rows
  const headerRow = 5
  const kindRow = 6
  const unitsRow = 7
  const firstBodyRow = 8
  sheet.text(headerRow, LABEL_COL, 'Account / group', styles.id('columnHeader'))
  const monthHeaderStyle = (forecast: boolean) => styles.custom({
    bold: true, fontSize: 10, align: 'right', verticalAlign: 'middle',
    textColor: forecast ? CFO_PALETTE.forecastText : CFO_PALETTE.headerText,
    backgroundColor: forecast ? CFO_PALETTE.forecastFill : CFO_PALETTE.headerFill,
    border: { bottom: { style: 'thin', color: CFO_PALETTE.border } },
  })
  const quarterHeaderStyle = styles.custom({ bold: true, fontSize: 10, align: 'right', verticalAlign: 'middle', textColor: CFO_PALETTE.headerText, backgroundColor: CFO_PALETTE.subtotalFill, border: { bottom: { style: 'thin', color: CFO_PALETTE.border } } })
  const yearHeaderStyle = styles.custom({ bold: true, fontSize: 10, align: 'right', verticalAlign: 'middle', textColor: CFO_PALETTE.brandText, backgroundColor: CFO_PALETTE.brandFill })
  for (const c of columns) {
    sheet.text(headerRow, c.col, c.label, c.kind === 'year' ? yearHeaderStyle : c.kind === 'quarter' ? quarterHeaderStyle : monthHeaderStyle(c.forecast))
    const tag = c.kind === 'year' ? 'Year' : c.kind === 'quarter' ? 'Qtr' : c.forecast ? 'Forecast' : 'Actual'
    sheet.text(kindRow, c.col, tag, c.kind === 'month' ? (c.forecast ? styles.id('caseLow') : styles.id('caseExpected')) : styles.id('note'))
  }
  sheet.text(unitsRow, LABEL_COL, `In ${input.currencySymbol ?? '$'}`, styles.id('unitsRow'))

  // The single bottom line gets the petrol accent band; the deep account detail
  // (level >= 2) is collapsed by default so the sheet opens as a clean summary that
  // expands to the full chart of accounts. The bottom line is often a *formula* row
  // (Net income / Ending cash), not is_total, so match it by label first.
  const bottomLineRe = statement.statementType === 'balance_sheet'
    ? /total liabilities and equity|total equity/i
    : statement.statementType === 'cash_flow'
      ? /ending cash|ending balance/i
      : /net income|net profit|net earnings|net loss/i
  let lastGrandIdx = -1
  statement.rows.forEach((r, i) => { if (bottomLineRe.test(r.label || '')) lastGrandIdx = i })
  if (lastGrandIdx < 0) statement.rows.forEach((r, i) => { if ((r.isTotal || r.isFormula) && Number(r.level || 0) <= 0) lastGrandIdx = i })
  const bottomLabelStyle = styles.custom({ fontFamily: CFO_FONTS.serif, bold: true, fontSize: 12, textColor: CFO_PALETTE.brandText, backgroundColor: CFO_PALETTE.brand, align: 'left', verticalAlign: 'middle' })
  const bottomNumberStyle = styles.custom({ fontFamily: CFO_FONTS.serif, bold: true, fontSize: 12, textColor: CFO_PALETTE.brandText, backgroundColor: CFO_PALETTE.brand, align: 'right', verticalAlign: 'middle', numberFormat: { formatCode: styles.numberFormats.currency } })

  // Body rows
  let row = firstBodyRow
  let endingCashRowIdx = -1
  let bsAssetsRowIdx = -1
  let bsLiabEquityRowIdx = -1
  let bsCashTotalIdx = -1
  let bsCashAnyIdx = -1
  statement.rows.forEach((r, rowIdx) => {
    const isBottom = rowIdx === lastGrandIdx
    const isGrand = r.isTotal && r.level <= 0
    const isSub = r.isTotal || r.rowType === 'group'
    const labelStyle = isBottom ? bottomLabelStyle : isGrand ? styles.id('total') : isSub ? styles.id('subtotal') : (r.level > 0 ? styles.id('rowLabelIndent') : styles.id('rowLabel'))
    const indent = '  '.repeat(Math.max(0, Math.min(4, r.level)))
    sheet.text(row, LABEL_COL, `${indent}${r.label}`, labelStyle)
    const agg = rowAggregation(statement.statementType, r.label)
    if (statement.statementType === 'cash_flow' && /ending|closing/i.test(r.label) && /cash|balance/i.test(r.label)) endingCashRowIdx = row
    if (statement.statementType === 'balance_sheet') {
      if (bsAssetsRowIdx < 0 && /total\s+assets/i.test(r.label)) bsAssetsRowIdx = row
      if (bsLiabEquityRowIdx < 0 && /total\s+liabilities\s+and\s+equity/i.test(r.label)) bsLiabEquityRowIdx = row
      if (/(total\s+)?(bank|cash and cash|cash)\b/i.test(r.label)) {
        if (bsCashAnyIdx < 0) bsCashAnyIdx = row
        if (r.isTotal && bsCashTotalIdx < 0) bsCashTotalIdx = row
      }
    }

    const numberStyleFor = (forecast: boolean) => isBottom ? bottomNumberStyle : isGrand ? styles.id('totalNumber') : isSub ? styles.id('subtotalNumber') : forecast ? styles.id('forecastNumber') : styles.id('actualNumber')
    const hasValueAt = (i: number) => num(r.byCase.high[i]) != null || num(r.byCase.expected[i]) != null || num(r.byCase.low[i]) != null

    for (const c of columns) {
      if (c.kind === 'month') {
        const i = c.periodIdxs[0]
        if (!hasValueAt(i)) continue
        const hi = num(r.byCase.high[i]) ?? 0
        const ex = num(r.byCase.expected[i]) ?? 0
        const lo = num(r.byCase.low[i]) ?? 0
        sheet.formula(row, c.col, caseChooseFormula(hi, ex, lo), ex, numberStyleFor(c.forecast))
        continue
      }
      // Quarter / year roll-up.
      const present = c.periodIdxs.filter(hasValueAt)
      if (present.length === 0) continue
      let formula: string
      let cached: number
      if (agg === 'first') {
        const ref = cellLocal(row, c.monthCols[0])
        formula = ref
        cached = num(r.byCase.expected[c.periodIdxs[0]]) ?? 0
      } else if (agg === 'last') {
        const ref = cellLocal(row, c.monthCols[c.monthCols.length - 1])
        formula = ref
        cached = num(r.byCase.expected[c.periodIdxs[c.periodIdxs.length - 1]]) ?? 0
      } else {
        formula = `SUM(${c.sumCols.map((cc) => cellLocal(row, cc)).join(',')})`
        cached = c.periodIdxs.reduce((s, i) => s + (num(r.byCase.expected[i]) ?? 0), 0)
      }
      sheet.formula(row, c.col, formula, cached, numberStyleFor(c.forecast))
    }
    row += 1
    // Collapsible row detail: group account rows under their totals; collapse the
    // deep chart-of-accounts detail (level >= 2) by default for a clean summary view.
    if (r.level > 0) sheet.rowOutline(row - 1, Math.min(7, r.level), { hidden: r.level >= 2 })
  })
  const lastBodyRow = row - 1

  // Column widths + outline grouping (open at the year roll-up; expand to qtr/month).
  sheet.colWidth(LABEL_COL, 44)
  for (const c of columns) {
    sheet.colWidth(c.col, c.kind === 'year' ? 15 : c.kind === 'quarter' ? 14 : 12)
    if (c.kind === 'month') sheet.colOutline(c.col, 2, { hidden: true })
    else if (c.kind === 'quarter') sheet.colOutline(c.col, 1, { hidden: true, collapsed: true })
    else sheet.colOutline(c.col, 0, { collapsed: true })
  }
  sheet.setSheetOutline({ summaryRight: true, summaryBelow: true, showSymbols: true })
  sheet.freeze(firstBodyRow, FIRST_PERIOD_COL)
  // No autofilter: the date-header dropdown arrows add no value on a forecast
  // statement and clutter the headers. Freeze panes + outline grouping above are
  // independent and stay. (Engine still supports autofilter for the AR/GL exports.)
  sheet.setPrintSettings({
    print_options: { grid_lines: false },
    page_margins: { left: 0.4, right: 0.4, top: 0.5, bottom: 0.5, header: 0.3, footer: 0.3 },
    page_setup: { orientation: 'landscape', fit_to_width: 1, fit_to_height: 0 },
  })

  // Cash flow: heatmap + min-cash flag across the ending-cash balance (month columns).
  if (statement.statementType === 'cash_flow' && endingCashRowIdx >= 0) {
    const monthCols = columns.filter((c) => c.kind === 'month').map((c) => c.col)
    if (monthCols.length > 0) {
      const c1 = monthCols[0]
      const c2 = monthCols[monthCols.length - 1]
      const R = endingCashRowIdx + 1 // 1-based
      const first = colLetterLocal(c1)
      const last = colLetterLocal(c2)
      sheet.addConditionalFormat({
        id: 'cf_cash_heatmap', kind: 'colorScale',
        ranges: [sheet.range(endingCashRowIdx, c1, endingCashRowIdx, c2)], priority: 2,
        colorScale: { stops: [
          { cfvo: { type: 'min' }, color: CFO_PALETTE.dangerFill },
          { cfvo: { type: 'percentile', val: 50 }, color: CFO_PALETTE.warnFill },
          { cfvo: { type: 'max' }, color: CFO_PALETTE.okFill },
        ] },
      })
      sheet.addConditionalFormat({
        id: 'cf_min_cash', kind: 'expression',
        ranges: [sheet.range(endingCashRowIdx, c1, endingCashRowIdx, c2)], priority: 1,
        formula1: `AND(${first}$${R}<>0,${first}$${R}=MIN($${first}$${R}:$${last}$${R}))`,
        format: { backgroundColor: CFO_PALETTE.dangerFill, textColor: CFO_PALETTE.dangerText, bold: true },
      })
      // Expose the ending-cash trajectory so the Cover KPIs tie to THIS statement
      // (the authoritative ML/BS cash), not the simplified driver model.
      const yearCols = columns.filter((c) => c.kind === 'year').map((c) => c.col)
      const finalCol = yearCols.length ? yearCols[yearCols.length - 1] : c2
      sheet.named('Stmt_End_Cash', endingCashRowIdx, finalCol)
      sheet.named('Stmt_End_Cash_Range', endingCashRowIdx, c1, endingCashRowIdx, c2)
    }
  }

  // Expose Balance-Sheet identity cells so the Cover can show a live "Balanced ✓"
  // check (Assets vs Liabilities+Equity, and CF ending cash vs BS cash). Year column =
  // period-end balance (rowAggregation returns 'last' for the balance sheet).
  if (statement.statementType === 'balance_sheet') {
    const monthCols = columns.filter((c) => c.kind === 'month').map((c) => c.col)
    const yearCols = columns.filter((c) => c.kind === 'year').map((c) => c.col)
    const finalCol = yearCols.length ? yearCols[yearCols.length - 1] : (monthCols.length ? monthCols[monthCols.length - 1] : lastCol)
    const bsCashRowIdx = bsCashTotalIdx >= 0 ? bsCashTotalIdx : bsCashAnyIdx
    if (bsAssetsRowIdx >= 0) sheet.named('BS_Total_Assets', bsAssetsRowIdx, finalCol)
    if (bsLiabEquityRowIdx >= 0) sheet.named('BS_Total_Liab_Equity', bsLiabEquityRowIdx, finalCol)
    if (bsCashRowIdx >= 0) sheet.named('BS_Cash', bsCashRowIdx, finalCol)
  }

  return sheet
}

function colLetterLocal(col: number): string {
  let n = Math.max(0, Math.floor(col))
  let out = ''
  for (;;) {
    out = String.fromCharCode(65 + (n % 26)) + out
    n = Math.floor(n / 26) - 1
    if (n < 0) break
  }
  return out
}

// ---------------------------------------------------------------------------
// Drivers sheet (editable drivers + scenario control + live driver model)
// ---------------------------------------------------------------------------

function buildDriversSheet(
  wb: WorkbookBuilder,
  styles: CfoWorkbookStyleRegistry,
  input: CfoForecastWorkbookInput,
): SheetWriter {
  const sheet = new SheetWriter(SHEET.drivers.id, SHEET.drivers.name)
  const drivers = input.drivers

  cfoSheetHeader(sheet, styles, input, {
    title: 'Drivers & Assumptions',
    subtitle: 'Edit the blue cells, or flip the scenario, and the model below recalculates live across every sheet.',
    lastCol: 8,
  })

  // Scenario control
  const scenarioRow = 5
  sheet.text(scenarioRow, 0, 'Scenario', styles.id('sectionHeader'))
  const scenarioList = wb.addValidation({
    kind: 'list',
    values: CFO_CASE_ORDER.map((c) => CFO_CASE_LABELS[c]),
    allow_blank: false,
    input_title: 'Scenario',
    input_message: 'Choose High, Expected or Low to re-flow the whole workbook.',
    error_style: 'stop',
  })
  sheet.put(scenarioRow, 1, { value: CFO_CASE_LABELS.expected, styleId: styles.id('inputText'), validationId: scenarioList, protection: { locked: false } })
  sheet.named('Scenario', scenarioRow, 1)
  // Scenario_List + Scenario_Index helper cells (kept to the far right, narrow)
  const helperCol = 10
  CFO_CASE_ORDER.forEach((c, i) => sheet.text(scenarioRow + i, helperCol, CFO_CASE_LABELS[c], styles.id('note')))
  sheet.named('Scenario_List', scenarioRow, helperCol, scenarioRow + CFO_CASE_ORDER.length - 1, helperCol)
  sheet.formula(scenarioRow + CFO_CASE_ORDER.length, helperCol, 'MATCH(Scenario,Scenario_List,0)', CFO_CASE_ORDER.indexOf('expected') + 1, styles.id('note'))
  sheet.named('Scenario_Index', scenarioRow + CFO_CASE_ORDER.length, helperCol)
  sheet.colWidth(helperCol, 11)

  // Driver table
  const tableHeaderRow = 7
  const headers = ['Driver', 'Unit', 'Low', 'Expected', 'High', 'Active', 'Baseline', 'Your actual', 'Note']
  headers.forEach((h, i) => sheet.text(tableHeaderRow, i, h, i >= 2 && i <= 7 ? styles.id('columnHeaderRight') : styles.id('columnHeader')))
  const firstDriverRow = tableHeaderRow + 1
  const lowCol = 2
  const expCol = 3
  const highCol = 4
  const activeCol = 5
  const baselineCol = 6
  const actualCol = 7
  const noteCol = 8

  drivers.forEach((d, i) => {
    const r = firstDriverRow + i
    const inputStyle = d.unit === 'percent' ? styles.id('inputPercent') : d.unit === 'days' ? styles.id('inputDays') : styles.custom({ fontSize: 10, bold: true, textColor: CFO_PALETTE.inputBlue, backgroundColor: CFO_PALETTE.inputFill, align: 'right', numberFormat: { formatCode: '0.0' } })
    const formulaStyle = d.unit === 'percent' ? styles.id('formulaPercent') : d.unit === 'days' ? styles.custom({ fontSize: 10, textColor: CFO_PALETTE.ink, align: 'right', numberFormat: { formatCode: '0" d"' } }) : styles.custom({ fontSize: 10, textColor: CFO_PALETTE.ink, align: 'right', numberFormat: { formatCode: '0.0' } })
    const unitLabel = d.unit === 'percent' ? '%' : d.unit === 'days' ? 'days' : 'ratio'
    // Driver label carries an authored cell comment (source/confidence). The
    // OOXML writer synthesizes the backing VML legacy drawing for it, so Excel
    // opens the file without a repair prompt. When the driver is grounded in the
    // company's own QuickBooks history, the comment quotes that provenance.
    const labelComment = d.provenanceNote
      ? `${d.description}\nSource: ${d.provenanceNote}`
      : `${d.description}\nSource: ML baseline (editable for what-if).`
    sheet.put(r, 0, { value: d.label, styleId: styles.id('rowLabel'), comment: labelComment })
    sheet.text(r, 1, unitLabel, styles.id('note'))
    const bound = wb.addValidation({
      kind: 'decimal', operator: 'between',
      formula1: String(d.min), formula2: String(d.max), allow_blank: false,
      input_title: d.label, input_message: d.description, error_style: 'warning',
    })
    sheet.put(r, lowCol, { value: d.low, styleId: inputStyle, validationId: bound, protection: { locked: false } })
    sheet.put(r, expCol, { value: d.expected, styleId: inputStyle, validationId: bound, protection: { locked: false } })
    sheet.put(r, highCol, { value: d.high, styleId: inputStyle, validationId: bound, protection: { locked: false } })
    // Active = CHOOSE(Scenario_Index, <High scenario>, Expected, <Low scenario>) — Scenario_List order [High, Expected, Low].
    // Oriented by cash-favourability: 'down' drivers (DSO, opex) take their LOW value in the High (optimistic) scenario;
    // 'neutral' drivers stay at Expected. Mirrors activeDriver() so cached values match the live recalc.
    const highCell = cellLocal(r, highCol)
    const expCell = cellLocal(r, expCol)
    const lowCell = cellLocal(r, lowCol)
    const optimisticCell = d.cashFavorable === 'neutral' ? expCell : d.cashFavorable === 'up' ? highCell : lowCell
    const pessimisticCell = d.cashFavorable === 'neutral' ? expCell : d.cashFavorable === 'up' ? lowCell : highCell
    sheet.formula(r, activeCol, `CHOOSE(Scenario_Index,${optimisticCell},${expCell},${pessimisticCell})`, d.expected, formulaStyle)
    sheet.named(d.namedRange, r, activeCol)
    sheet.number(r, baselineCol, d.baseline, formulaStyle)
    // "Your actual" — the company's own trailing-12-mo figure (a static reference
    // fact, not part of the scenario CHOOSE recalc). Muted so it reads as context.
    const actualRefStyle = styles.custom({ fontSize: 10, textColor: CFO_PALETTE.subtle, align: 'right', numberFormat: { formatCode: d.unit === 'percent' ? '0.0%' : d.unit === 'days' ? '0" d"' : '0.0' } })
    if (typeof d.historicalActual === 'number' && Number.isFinite(d.historicalActual)) {
      sheet.put(r, actualCol, { value: d.historicalActual, styleId: actualRefStyle, comment: d.provenanceNote ?? undefined })
    } else {
      sheet.text(r, actualCol, '—', styles.id('note'))
    }
    sheet.text(r, noteCol, d.note ?? d.description, styles.id('note'))
    // Highlight when Active deviates from baseline
    sheet.addConditionalFormat({
      id: `drv_dev_${d.key}`,
      kind: 'expression',
      ranges: [sheet.range(r, activeCol, r, activeCol)],
      priority: 1,
      formula1: `ABS(${cellLocal(r, activeCol)}-${cellLocal(r, baselineCol)})>0.0001`,
      format: { backgroundColor: CFO_PALETTE.warnFill, textColor: CFO_PALETTE.warnText },
    })
  })

  // "Your actual" provenance footnote — explains the reference column + flags sparse history.
  const anyActual = drivers.some((d) => typeof d.historicalActual === 'number' && Number.isFinite(d.historicalActual))
  const sparseHistory = drivers.some((d) => d.sparseHistory)
  const footRow = firstDriverRow + drivers.length
  if (anyActual) {
    sheet.text(footRow, 0, sparseHistory
      ? '"Your actual" = your trailing-12-month QuickBooks figures. Based on fewer than 6 closed months — treat as directional until more history accrues.'
      : '"Your actual" = your trailing-12-month QuickBooks figures, shown for reference. The editable cells above are the what-if drivers.',
      styles.id('note'))
    sheet.merge(footRow, 0, footRow, noteCol)
  }

  // Seed inputs (editable): base revenue + opening cash
  const seedRow = firstDriverRow + drivers.length + 1
  sheet.text(seedRow, 0, 'Base monthly revenue (anchor)', styles.id('rowLabel'))
  sheet.put(seedRow, activeCol, { value: input.seed.baseMonthlyRevenue, styleId: styles.id('inputNumber'), protection: { locked: false } })
  sheet.named('Base_Revenue', seedRow, activeCol)
  sheet.text(seedRow + 1, 0, 'Opening cash (anchor)', styles.id('rowLabel'))
  sheet.put(seedRow + 1, activeCol, { value: input.seed.openingCash, styleId: styles.id('inputNumber'), protection: { locked: false } })
  sheet.named('Opening_Cash', seedRow + 1, activeCol)

  // -------- Live driver model (monthly projection) --------
  const months = Math.max(1, Math.floor(input.seed.horizonMonths))
  const expectedProjection = projectDriverModel(drivers, input.seed, 'expected')
  // Anchor the live model's ENDING CASH to the ONLINE forecast's cash (the harvested
  // statement on input.statements) rather than letting it project a second, divergent
  // path. At the exported (default) drivers the model reproduces the online number
  // exactly; editing a driver flexes it by the model's marginal cash effect only. This
  // is the "one model" guarantee: the offline what-if starts from the online truth.
  const cfBaseline = input.statements.find((s) => s.statementType === 'cash_flow')
  const cfEndRow = cfBaseline?.rows.find((r) => /ending|closing/i.test(r.label) && /cash|balance/i.test(r.label))
  const cfFirstForecast = cfBaseline ? cfBaseline.periodKinds.findIndex((k) => k === 'forecast') : -1
  const onlineCashAt = (i: number): number => {
    if (cfEndRow && cfFirstForecast >= 0) {
      const v = cfEndRow.byCase.expected[cfFirstForecast + i]
      if (typeof v === 'number' && Number.isFinite(v)) return v
    }
    return expectedProjection.endingCash[i]
  }
  const modelHeaderRow = seedRow + 3
  sheet.text(modelHeaderRow, 0, 'Driver model — ending cash anchored to the online forecast; edits flex it (recalculates live)', styles.id('sectionHeader'))
  sheet.merge(modelHeaderRow, 0, modelHeaderRow, months + 1)
  const modelPeriodRow = modelHeaderRow + 1
  sheet.text(modelPeriodRow, 0, 'Month', styles.id('columnHeader'))
  for (let t = 1; t <= months; t += 1) sheet.text(modelPeriodRow, t, `M${t}`, styles.id('columnHeaderRight'))

  // Row layout for the model
  const rows = {
    revenue: modelPeriodRow + 1,
    cogs: modelPeriodRow + 2,
    grossProfit: modelPeriodRow + 3,
    opex: modelPeriodRow + 4,
    ebitda: modelPeriodRow + 5,
    ar: modelPeriodRow + 6,
    ap: modelPeriodRow + 7,
    cashFromOps: modelPeriodRow + 8,
    endingCash: modelPeriodRow + 9,
  }
  sheet.text(rows.revenue, 0, 'Revenue', styles.id('rowLabel'))
  sheet.text(rows.cogs, 0, 'COGS', styles.id('rowLabelIndent'))
  sheet.text(rows.grossProfit, 0, 'Gross profit', styles.id('subtotal'))
  sheet.text(rows.opex, 0, 'Operating expense', styles.id('rowLabelIndent'))
  sheet.text(rows.ebitda, 0, 'EBITDA', styles.id('subtotal'))
  sheet.text(rows.ar, 0, 'Accounts receivable', styles.id('rowLabelIndent'))
  sheet.text(rows.ap, 0, 'Accounts payable', styles.id('rowLabelIndent'))
  sheet.text(rows.cashFromOps, 0, 'Cash from operations', styles.id('rowLabel'))
  sheet.text(rows.endingCash, 0, 'Ending cash', styles.id('total'))

  const moneyStyle = styles.id('formula')
  const subMoneyStyle = styles.id('subtotalNumber')
  const totalMoneyStyle = styles.id('totalNumber')
  for (let t = 1; t <= months; t += 1) {
    const seas = seasonalIndexForMonthOffset(t - 1)
    const i = t - 1
    // Revenue
    sheet.formula(rows.revenue, t, `Base_Revenue*(1+Sales_Growth/12)^${t}*(1+Seasonality*${seas})`, expectedProjection.revenue[i], moneyStyle)
    // COGS = Revenue*(1-Gross_Margin)
    sheet.formula(rows.cogs, t, `${cellLocal(rows.revenue, t)}*(1-Gross_Margin)`, expectedProjection.cogs[i], moneyStyle)
    // Gross profit
    sheet.formula(rows.grossProfit, t, `${cellLocal(rows.revenue, t)}-${cellLocal(rows.cogs, t)}`, expectedProjection.grossProfit[i], subMoneyStyle)
    // Opex
    sheet.formula(rows.opex, t, `${cellLocal(rows.revenue, t)}*Opex_Pct`, expectedProjection.opex[i], moneyStyle)
    // EBITDA
    sheet.formula(rows.ebitda, t, `${cellLocal(rows.grossProfit, t)}-${cellLocal(rows.opex, t)}`, expectedProjection.ebitda[i], subMoneyStyle)
    // AR / AP
    sheet.formula(rows.ar, t, `${cellLocal(rows.revenue, t)}*DSO_Days/30`, expectedProjection.ar[i], moneyStyle)
    sheet.formula(rows.ap, t, `${cellLocal(rows.cogs, t)}*DPO_Days/30`, expectedProjection.ap[i], moneyStyle)
    // Cash from ops = EBITDA - dAR + dAP
    const prevAr = t === 1 ? 'Base_Revenue*DSO_Days/30' : cellLocal(rows.ar, t - 1)
    const prevAp = t === 1 ? 'Base_Revenue*(1-Gross_Margin)*DPO_Days/30' : cellLocal(rows.ap, t - 1)
    sheet.formula(rows.cashFromOps, t, `${cellLocal(rows.ebitda, t)}-(${cellLocal(rows.ar, t)}-(${prevAr}))+(${cellLocal(rows.ap, t)}-(${prevAp}))`, expectedProjection.cashFromOps[i], moneyStyle)
    // Ending cash = ONLINE baseline + the model's MARGINAL driver effect (active cash
    // from ops minus the baseline cash from ops), accumulated. At default drivers the
    // marginal term is 0 so this equals the online cash exactly; edits flex it.
    const onlineBase = onlineCashAt(i)
    const deltaCfo = `(${cellLocal(rows.cashFromOps, t)}-(${expectedProjection.cashFromOps[i]}))`
    const endingFormula = t === 1
      ? `${onlineBase}+${deltaCfo}`
      : `${onlineBase}+(${cellLocal(rows.endingCash, t - 1)}-(${onlineCashAt(i - 1)}))+${deltaCfo}`
    sheet.formula(rows.endingCash, t, endingFormula, onlineBase, totalMoneyStyle)
  }

  // KPI named ranges sourced from the live model (single source for the Cover tiles)
  const lastT = months
  sheet.named('Revenue_Latest', rows.revenue, lastT)
  sheet.named('EBITDA_Latest', rows.ebitda, lastT)
  sheet.named('Cash_Balance', rows.endingCash, lastT)

  // Derived KPI cells (burn, runway, min cash) placed in a compact block
  const kpiRow = rows.endingCash + 2
  sheet.text(kpiRow, 0, 'Monthly burn (avg last 3)', styles.id('rowLabel'))
  const lastStart = Math.max(1, months - 2)
  sheet.formula(kpiRow, activeCol, `MAX(0,-AVERAGE(${cellLocal(rows.cashFromOps, lastStart)}:${cellLocal(rows.cashFromOps, months)}))`, Math.max(0, -average(expectedProjection.cashFromOps.slice(Math.max(0, months - 3)))), styles.id('formula'))
  sheet.named('Monthly_Burn', kpiRow, activeCol)
  sheet.text(kpiRow + 1, 0, 'Cash runway (months)', styles.id('rowLabel'))
  sheet.formula(kpiRow + 1, activeCol, 'IF(Monthly_Burn>0,Cash_Balance/Monthly_Burn,999)', runwayMonths(expectedProjection), styles.custom({ fontSize: 10, bold: true, textColor: CFO_PALETTE.ink, align: 'right', numberFormat: { formatCode: '0.0' } }))
  sheet.named('Runway_Months', kpiRow + 1, activeCol)
  sheet.text(kpiRow + 2, 0, 'Minimum ending cash', styles.id('rowLabel'))
  sheet.formula(kpiRow + 2, activeCol, `MIN(${cellLocal(rows.endingCash, 1)}:${cellLocal(rows.endingCash, months)})`, Math.min(...expectedProjection.endingCash), styles.id('formula'))
  sheet.named('Min_Cash', kpiRow + 2, activeCol)

  // In-cell data bar on the ending-cash row (magnitude at a glance, recalcs live).
  sheet.addConditionalFormat({
    id: 'drv_endcash_bar',
    kind: 'dataBar',
    ranges: [sheet.range(rows.endingCash, 1, rows.endingCash, months)],
    priority: 5,
    dataBar: { color: CFO_PALETTE.brand, min: { type: 'min' }, max: { type: 'max' } },
  })

  // Hero combo chart: cash from operations (bars) + ending cash (line, secondary axis).
  const chartTop = kpiRow + 4
  const cats = sheet.ref(modelPeriodRow, 1, modelPeriodRow, months)
  sheet.chart('chart_driver_cash', { r1: chartTop, c1: 0, r2: chartTop + 17, c2: Math.max(8, months) }, {
    title: 'Cash from operations & ending cash (live driver model)',
    legend: 'b',
    catTitle: 'Forecast month',
    valTitle: 'Cash from ops',
    valTitle2: 'Ending cash',
    series: [
      { name: 'Cash from ops', type: 'col', color: CFO_PALETTE.brand, categoriesRef: cats, valuesRef: sheet.ref(rows.cashFromOps, 1, rows.cashFromOps, months) },
      { name: 'Ending cash', type: 'line', color: CFO_PALETTE.inputBlue, secondaryAxis: true, smooth: true, categoriesRef: cats, valuesRef: sheet.ref(rows.endingCash, 1, rows.endingCash, months) },
    ],
  })

  // Layout polish
  sheet.freeze(0, 1)
  sheet.colWidth(0, 30)
  sheet.colWidth(1, 8)
  sheet.colWidths(2, [12, 12, 12, 13, 12])
  sheet.colWidth(actualCol, 13)
  sheet.colWidth(noteCol, 40)
  for (let t = 1; t <= months; t += 1) sheet.colWidth(t, 11)
  // Protect formulas but keep the blue input cells editable. Passing the explicit
  // allow_select_* flags tripped an inverted polarity in the OOXML emitter
  // (selectUnlockedCells="1" actually BLOCKS selection per ECMA-376), which locked
  // the whole sheet. Bare protect() emits <sheetProtection sheet="1"/>: only
  // locked="1" cells are protected; the locked="0" blue inputs stay selectable + editable.
  sheet.protect({})
  return sheet
}

function average(values: number[]): number {
  if (values.length === 0) return 0
  return values.reduce((a, b) => a + b, 0) / values.length
}

function runwayMonths(p: DriverModelProjection): number {
  const burn = Math.max(0, -average(p.cashFromOps.slice(Math.max(0, p.months - 3))))
  const cash = p.endingCash[p.endingCash.length - 1] ?? 0
  return burn > 0 ? cash / burn : 999
}

function cellLocal(row: number, col: number): string {
  return `$${colLetterLocal(col)}$${row + 1}`
}

// ---------------------------------------------------------------------------
// Cover & dashboard
// ---------------------------------------------------------------------------

function buildCoverSheet(
  wb: WorkbookBuilder,
  styles: CfoWorkbookStyleRegistry,
  input: CfoForecastWorkbookInput,
): SheetWriter {
  const sheet = new SheetWriter(SHEET.cover.id, SHEET.cover.name)
  const driversName = SHEET.drivers.name

  cfoSheetHeader(sheet, styles, input, {
    title: 'Cash-Flow Forecast',
    subtitle: `Three-way financial forecast · ${input.grainLabel} · actuals to ${input.anchorPeriod ?? '—'} then ML forecast · ${input.currencySymbol ?? '$'}`,
    lastCol: 7,
  })

  // Provisional banner — only when readiness is set and below "ready". Full-width,
  // at the top of the body so the honesty caveat is seen first. Never manufactured
  // from missing metadata (the headless path leaves readiness null → no banner).
  const readinessStatus = input.vintage?.readinessStatus ?? null
  const isProvisional = !!readinessStatus && readinessStatus.toLowerCase() !== 'ready'
  let bodyTop = 5
  if (isProvisional) {
    const closed = input.vintage?.closedMonthsCount ?? null
    const asOf = (input.vintage?.dataAsOf ?? input.anchorPeriod ?? '').slice(0, 10) || '—'
    sheet.text(bodyTop, 0,
      `Provisional — based on ${closed != null ? `${closed} month${closed === 1 ? '' : 's'}` : 'a limited window'} of history as of ${asOf}; not yet validated against closed actuals.`,
      styles.custom({ fontFamily: CFO_FONTS.sans, bold: true, fontSize: 10, textColor: CFO_PALETTE.warnText, backgroundColor: CFO_PALETTE.warnFill, align: 'left', verticalAlign: 'middle' }))
    sheet.merge(bodyTop, 0, bodyTop, 7)
    sheet.rowHeight(bodyTop, 22)
    bodyTop += 2
  }

  // Metadata strip
  const metaRow = bodyTop
  const meta: Array<[string, string]> = [
    ['Anchor period', input.anchorPeriod ?? '—'],
    ['Grain', input.grainLabel],
    ['Version', input.version ?? 'v1.0'],
    ['Generated', (input.generatedAtIso ?? '').slice(0, 10) || '—'],
  ]
  meta.forEach(([k, v], i) => {
    sheet.text(metaRow, i * 2, k, styles.id('kpiLabel'))
    sheet.text(metaRow + 1, i * 2, v, styles.id('coverMeta'))
  })

  // Data-vintage line — the "as of" date, the quotable model/package id, and how
  // many closed months feed it. Gives the one-click deliverable a version identity.
  if (input.vintage) {
    const asOf = (input.vintage.dataAsOf ?? input.anchorPeriod ?? '').slice(0, 10) || '—'
    const pkg = input.vintage.packageId ? `Model ${String(input.vintage.packageId).slice(0, 8)}` : null
    const closedMonths = input.vintage.closedMonthsCount
    const closed = closedMonths != null ? `${closedMonths} closed month${closedMonths === 1 ? '' : 's'} of history` : null
    const parts = [`Data as of ${asOf}`, pkg, closed].filter(Boolean) as string[]
    sheet.text(metaRow + 2, 0, parts.join('   ·   '), styles.id('note'))
    sheet.merge(metaRow + 2, 0, metaRow + 2, 7)
  }

  // Scenario toggle (mirrors the Drivers control via the shared named range)
  const scenarioRow = metaRow + 3
  sheet.text(scenarioRow, 0, 'Scenario', styles.id('sectionHeader'))
  sheet.merge(scenarioRow, 0, scenarioRow, 1)
  // Read-only mirror of the live scenario; the editable control lives on the Drivers sheet.
  sheet.formula(scenarioRow, 2, 'Scenario', CFO_CASE_LABELS.expected, styles.id('inputText'))
  sheet.text(scenarioRow, 3, '◀ change on the Drivers sheet', styles.id('note'))

  // Three-statement balance check (Assets = Liabilities + Equity, and Cash-Flow ending
  // cash = Balance-Sheet cash). Formula-driven so it recomputes live on a driver edit;
  // references only named cells that the BS/CF sheets register, guarded so it never
  // dangles. IFERROR degrades a missing term to 0 rather than #NAME?.
  const bsStmt = input.statements.find((s) => s.statementType === 'balance_sheet')
  const cfStmt = input.statements.find((s) => s.statementType === 'cash_flow')
  const hasBsAssets = !!bsStmt?.rows.some((r) => /total\s+assets/i.test(r.label))
  const hasBsLE = !!bsStmt?.rows.some((r) => /total\s+liabilities\s+and\s+equity/i.test(r.label))
  const hasBsCashLine = !!bsStmt?.rows.some((r) => /(total\s+)?(bank|cash and cash|cash)\b/i.test(r.label))
  const hasCfEndCash = !!cfStmt?.rows.some((r) => /ending|closing/i.test(r.label) && /cash|balance/i.test(r.label))
  const balTerms: string[] = []
  if (hasBsAssets && hasBsLE) balTerms.push('ABS(IFERROR(BS_Total_Assets-BS_Total_Liab_Equity,0))')
  if (hasCfEndCash && hasBsCashLine) balTerms.push('ABS(IFERROR(Stmt_End_Cash-BS_Cash,0))')
  if (balTerms.length) {
    const integrity = checkThreeStatementIntegrity(input.statements)
    const balRow = metaRow + 4
    const outOfBalance = Math.round(integrity.worstAbsDiff)
    const sym = input.currencySymbol ?? '$'
    sheet.text(balRow, 0, 'Balance check', styles.id('sectionHeader'))
    sheet.merge(balRow, 0, balRow, 1)
    const statusCached = integrity.ok ? 'Balanced ✓' : `⚠ Out of balance by ${sym}${outOfBalance.toLocaleString()}`
    sheet.formula(
      balRow, 2,
      'IF(Balance_Out_Of_Balance<=1,"Balanced ✓","⚠ Out of balance by "&TEXT(Balance_Out_Of_Balance,"$#,##0"))',
      statusCached,
      styles.custom({ fontFamily: CFO_FONTS.sans, bold: true, fontSize: 11, textColor: CFO_PALETTE.ink, align: 'left', verticalAlign: 'middle' }),
    )
    sheet.merge(balRow, 2, balRow, 5)
    sheet.formula(
      balRow, 6,
      balTerms.length > 1 ? `MAX(${balTerms.join(',')})` : balTerms[0],
      outOfBalance,
      styles.custom({ fontSize: 9, textColor: CFO_PALETTE.faint, align: 'right', verticalAlign: 'middle', numberFormat: { formatCode: styles.numberFormats.currency } }),
    )
    sheet.merge(balRow, 6, balRow, 7)
    sheet.named('Balance_Out_Of_Balance', balRow, 6)
    sheet.addConditionalFormat({
      id: 'cover_balance_ok', kind: 'expression', priority: 2,
      ranges: [sheet.range(balRow, 2, balRow, 2)],
      formula1: 'Balance_Out_Of_Balance<=1',
      format: { textColor: CFO_PALETTE.okText },
    })
    sheet.addConditionalFormat({
      id: 'cover_balance_bad', kind: 'expression', priority: 1,
      ranges: [sheet.range(balRow, 2, balRow, 2)],
      formula1: 'Balance_Out_Of_Balance>1',
      format: { textColor: CFO_PALETTE.dangerText, bold: true },
    })
  }

  // Tie the headline cash KPIs to the Cash Flow statement (authoritative), not the
  // simplified driver model — otherwise the Cover and the statements disagree.
  const cf = input.statements.find((s) => s.statementType === 'cash_flow')
  const endRow = cf?.rows.find((r) => /ending|closing/i.test(r.label) && /cash|balance/i.test(r.label))
  const cashSeries = endRow
    ? cf!.periods.map((_p, i) => num(endRow.byCase.expected[i])).filter((v): v is number => v != null)
    : []
  const hasStmtCash = cashSeries.length > 0
  const endCashCached = hasStmtCash ? cashSeries[cashSeries.length - 1] : 0
  const minCashCached = hasStmtCash ? Math.min(...cashSeries) : 0

  // KPI tiles (2 x 4)
  const tiles: Array<{ label: string; formula: string; cached: number; unit: string; numberFormat: string; big?: boolean }> = [
    { label: 'CASH RUNWAY', formula: 'Runway_Months', cached: 0, unit: 'months', numberFormat: '0.0', big: true },
    { label: 'CASH BALANCE', formula: hasStmtCash ? 'Stmt_End_Cash' : 'Cash_Balance', cached: endCashCached, unit: 'end of horizon', numberFormat: styles.numberFormats.currency },
    { label: 'MONTHLY BURN', formula: 'Monthly_Burn', cached: 0, unit: 'avg last 3 mo', numberFormat: styles.numberFormats.currency },
    { label: 'MIN CASH', formula: hasStmtCash ? 'MIN(Stmt_End_Cash_Range)' : 'Min_Cash', cached: minCashCached, unit: 'lowest month', numberFormat: styles.numberFormats.currency },
    { label: 'REVENUE', formula: 'Revenue_Latest', cached: 0, unit: 'latest month', numberFormat: styles.numberFormats.currency },
    { label: 'EBITDA', formula: 'EBITDA_Latest', cached: 0, unit: 'latest month', numberFormat: styles.numberFormats.currency },
    { label: 'GROSS MARGIN', formula: 'Gross_Margin', cached: 0, unit: 'active scenario', numberFormat: styles.numberFormats.percent },
    { label: 'SALES GROWTH', formula: 'Sales_Growth', cached: 0, unit: 'annual', numberFormat: styles.numberFormats.percent },
  ]
  const tileTop = metaRow + 6
  const tileW = 2
  const tileH = 3
  // Thin brass rule above each card delineates the KPI grid (the "card" feel).
  const cardLabelStyle = styles.custom({ fontFamily: CFO_FONTS.sans, bold: true, fontSize: 9, textColor: CFO_PALETTE.faint, align: 'left', verticalAlign: 'top', border: { top: { style: 'thin', color: CFO_PALETTE.brass } } })
  tiles.forEach((tile, i) => {
    const r = tileTop + Math.floor(i / 4) * tileH
    const c = (i % 4) * tileW
    sheet.text(r, c, tile.label, cardLabelStyle)
    sheet.put(r, c + 1, { value: '', styleId: cardLabelStyle })
    sheet.merge(r, c, r, c + 1)
    const valueStyle = styles.custom({
      fontFamily: CFO_FONTS.serif, fontSize: tile.big ? 28 : 18, textColor: CFO_PALETTE.ink, align: 'left', verticalAlign: 'middle',
      numberFormat: { formatCode: tile.numberFormat },
    })
    sheet.formula(r + 1, c, tile.formula, tile.cached, valueStyle)
    sheet.merge(r + 1, c, r + 1, c + 1)
    sheet.text(r + 2, c, tile.unit, styles.id('kpiUnit'))
    sheet.merge(r + 2, c, r + 2, c + 1)
  })
  // Runway tile conditional color (green >=12, amber 6-12, red <6)
  sheet.addConditionalFormat({
    id: 'kpi_runway_red', kind: 'expression', priority: 1,
    ranges: [sheet.range(tileTop + 1, 0, tileTop + 1, 1)],
    formula1: `${cellLocal(tileTop + 1, 0)}<6`,
    format: { textColor: CFO_PALETTE.dangerText },
  })
  sheet.addConditionalFormat({
    id: 'kpi_runway_amber', kind: 'expression', priority: 2,
    ranges: [sheet.range(tileTop + 1, 0, tileTop + 1, 1)],
    formula1: `AND(${cellLocal(tileTop + 1, 0)}>=6,${cellLocal(tileTop + 1, 0)}<12)`,
    format: { textColor: CFO_PALETTE.warnText },
  })
  sheet.addConditionalFormat({
    id: 'kpi_runway_green', kind: 'expression', priority: 3,
    ranges: [sheet.range(tileTop + 1, 0, tileTop + 1, 1)],
    formula1: `${cellLocal(tileTop + 1, 0)}>=12`,
    format: { textColor: CFO_PALETTE.okText },
  })

  // Legend
  const legendRow = tileTop + 2 * tileH + 1
  sheet.text(legendRow, 0, 'How to read this workbook', styles.id('sectionHeader'))
  sheet.merge(legendRow, 0, legendRow, 5)
  const legendItems = [
    'Blue cells are editable inputs (drivers); ink cells are formulas; petrol cells link across sheets.',
    'Softly tinted columns are forecast periods; plain columns are actuals.',
    `Flip the scenario on the ${driversName} sheet to switch High / Expected / Low across every sheet.`,
    'Statements open at the yearly roll-up — use the +/- controls to expand to quarters, months and account detail.',
  ]
  legendItems.forEach((t, i) => {
    sheet.text(legendRow + 1 + i, 0, `•  ${t}`, styles.id('legend'))
    sheet.merge(legendRow + 1 + i, 0, legendRow + 1 + i, 7)
  })

  sheet.colWidths(0, [18, 18, 18, 18, 18, 18, 18, 18])
  sheet.select()
  sheet.setPrintSettings({ print_options: { grid_lines: false }, page_setup: { orientation: 'landscape', fit_to_width: 1, fit_to_height: 1 } })
  return sheet
}

// ---------------------------------------------------------------------------
// Scenario comparison
// ---------------------------------------------------------------------------

function buildScenarioSheet(
  wb: WorkbookBuilder,
  styles: CfoWorkbookStyleRegistry,
  input: CfoForecastWorkbookInput,
): SheetWriter {
  const sheet = new SheetWriter(SHEET.scenario.id, SHEET.scenario.name)
  cfoSheetHeader(sheet, styles, input, {
    title: 'Scenario Comparison',
    subtitle: 'ML prediction bands shown side by side — not independent bull / base / bear scenarios. See Methodology.',
    lastCol: 4,
  })

  const headerRow = 5
  sheet.text(headerRow, 0, 'Metric', styles.id('columnHeader'))
  CFO_CASE_ORDER.forEach((c, i) => sheet.text(headerRow, 1 + i, CFO_CASE_LABELS[c], i === 0 ? styles.id('caseHigh') : i === 1 ? styles.id('caseExpected') : styles.id('caseLow')))

  const metrics: Array<{ label: string; cases: Record<CfoCase, number>; fmt: string }> = []
  const proj: Record<CfoCase, DriverModelProjection> = {
    high: projectDriverModel(input.drivers, input.seed, 'high'),
    expected: projectDriverModel(input.drivers, input.seed, 'expected'),
    low: projectDriverModel(input.drivers, input.seed, 'low'),
  }
  const last = (arr: number[]) => arr[arr.length - 1] ?? 0
  metrics.push({ label: 'Revenue (latest month)', cases: { high: last(proj.high.revenue), expected: last(proj.expected.revenue), low: last(proj.low.revenue) }, fmt: styles.numberFormats.currency })
  metrics.push({ label: 'EBITDA (latest month)', cases: { high: last(proj.high.ebitda), expected: last(proj.expected.ebitda), low: last(proj.low.ebitda) }, fmt: styles.numberFormats.currency })
  metrics.push({ label: 'Ending cash', cases: { high: last(proj.high.endingCash), expected: last(proj.expected.endingCash), low: last(proj.low.endingCash) }, fmt: styles.numberFormats.currency })
  metrics.push({ label: 'Minimum cash', cases: { high: Math.min(...proj.high.endingCash), expected: Math.min(...proj.expected.endingCash), low: Math.min(...proj.low.endingCash) }, fmt: styles.numberFormats.currency })
  metrics.push({ label: 'Runway (months)', cases: { high: runwayMonths(proj.high), expected: runwayMonths(proj.expected), low: runwayMonths(proj.low) }, fmt: '0.0' })

  // Every metric here is "more is better"; orient so the High column is never
  // below the Low column (defends against any residual band inversion).
  metrics.forEach((m) => {
    const o = orientBand(m.cases.low, m.cases.expected, m.cases.high)
    m.cases.low = o.low
    m.cases.high = o.high
  })

  metrics.forEach((m, i) => {
    const r = headerRow + 1 + i
    sheet.text(r, 0, m.label, styles.id('rowLabel'))
    CFO_CASE_ORDER.forEach((c, j) => {
      sheet.number(r, 1 + j, m.cases[c], styles.custom({ fontSize: 10, textColor: CFO_PALETTE.ink, align: 'right', numberFormat: { formatCode: m.fmt } }))
    })
  })

  const endCashIdx = metrics.findIndex((m) => /ending cash/i.test(m.label))
  if (endCashIdx >= 0) {
    const r = headerRow + 1 + endCashIdx
    sheet.chart('chart_scenario_cash', { r1: headerRow, c1: 5, r2: headerRow + 14, c2: 12 }, {
      title: 'Ending cash by scenario',
      legend: 'none',
      valTitle: `${input.currencySymbol ?? '$'} cash`,
      series: [{ name: 'Ending cash', type: 'col', color: CFO_PALETTE.brand, categoriesRef: sheet.ref(headerRow, 1, headerRow, 3), valuesRef: sheet.ref(r, 1, r, 3) }],
    })
  }

  sheet.freeze(headerRow + 1, 1)
  sheet.colWidth(0, 28)
  sheet.colWidths(1, [16, 16, 16])
  return sheet
}

// ---------------------------------------------------------------------------
// Forecast Range — widening-uncertainty fan / band chart
// ---------------------------------------------------------------------------

function addMonthsLabel(anchorPeriod: string | null, offset: number): string {
  const m = /^(\d{4})-(\d{2})/.exec(String(anchorPeriod ?? ''))
  if (!m) return `M${offset}`
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  const base = (Number(m[1]) * 12 + (Number(m[2]) - 1)) + offset
  return `${months[((base % 12) + 12) % 12]} ${Math.floor(base / 12)}`
}

function buildForecastRangeSheet(
  styles: CfoWorkbookStyleRegistry,
  input: CfoForecastWorkbookInput,
): SheetWriter {
  const sheet = new SheetWriter(SHEET.range.id, SHEET.range.name)
  cfoSheetHeader(sheet, styles, input, {
    title: 'Forecast Range — cash uncertainty',
    subtitle: 'The shaded band is the Low–High prediction range; the line is the Expected case. The further out, the wider the band.',
    lastCol: 7,
  })

  // Prefer the real ML cash-flow ending-cash bands; fall back to the driver model.
  const cf = input.statements.find((s) => s.statementType === 'cash_flow')
  const endRow = cf?.rows.find((r) => /ending|closing/i.test(r.label) && /cash/i.test(r.label))
  let labels: string[]
  let low: number[]
  let expected: number[]
  let high: number[]
  if (cf && endRow) {
    labels = cf.periods.map(fmtMonthLabel)
    const at = (arr: Array<number | null>, i: number) => (typeof arr[i] === 'number' && Number.isFinite(arr[i] as number) ? (arr[i] as number) : 0)
    const oriented = cf.periods.map((_p, i) => orientBand(at(endRow.byCase.low, i), at(endRow.byCase.expected, i), at(endRow.byCase.high, i)))
    low = oriented.map((b) => b.low)
    expected = oriented.map((b) => b.expected)
    high = oriented.map((b) => b.high)
  } else {
    const months = Math.max(1, Math.floor(input.seed.horizonMonths))
    labels = Array.from({ length: months }, (_v, i) => addMonthsLabel(input.anchorPeriod, i + 1))
    low = projectDriverModel(input.drivers, input.seed, 'low').endingCash
    expected = projectDriverModel(input.drivers, input.seed, 'expected').endingCash
    high = projectDriverModel(input.drivers, input.seed, 'high').endingCash
  }

  const headerRow = 5
  const firstDataRow = 6
  const headers = ['Forecast month', 'Low', 'Expected', 'High', 'Low→Exp band', 'Exp→High band']
  headers.forEach((h, i) => sheet.text(headerRow, i, h, i === 0 ? styles.id('columnHeader') : styles.id('columnHeaderRight')))
  const moneyStyle = styles.custom({ fontSize: 10, textColor: CFO_PALETTE.ink, align: 'right', numberFormat: { formatCode: styles.numberFormats.currency } })
  const bandStyle = styles.custom({ fontSize: 10, textColor: CFO_PALETTE.faint, align: 'right', numberFormat: { formatCode: styles.numberFormats.currency } })
  labels.forEach((label, i) => {
    const r = firstDataRow + i
    sheet.text(r, 0, label, styles.id('rowLabel'))
    sheet.number(r, 1, low[i] ?? 0, moneyStyle)
    sheet.number(r, 2, expected[i] ?? 0, moneyStyle)
    sheet.number(r, 3, high[i] ?? 0, moneyStyle)
    // Band helper columns (stacked-area construction): base Low + (Exp−Low) + (High−Exp) = High.
    // Use MIN/MAX of the Low/High cells so the band is non-zero even if a cell pair
    // is ever inverted (otherwise the fan collapses to the single Expected line).
    const loCell = cellLocal(r, 1)
    const exCell = cellLocal(r, 2)
    const hiCell = cellLocal(r, 3)
    sheet.formula(r, 4, `MAX(0,${exCell}-MIN(${loCell},${hiCell}))`, Math.max(0, (expected[i] ?? 0) - Math.min(low[i] ?? 0, high[i] ?? 0)), bandStyle)
    sheet.formula(r, 5, `MAX(0,MAX(${loCell},${hiCell})-${exCell})`, Math.max(0, Math.max(low[i] ?? 0, high[i] ?? 0) - (expected[i] ?? 0)), bandStyle)
  })
  const lastDataRow = firstDataRow + labels.length - 1
  const cats = sheet.ref(firstDataRow, 0, lastDataRow, 0)

  // Stacked-area fan: invisible Low base + two translucent bands + bold Expected line.
  sheet.chart('chart_fan', { r1: headerRow, c1: 7, r2: headerRow + 22, c2: 18 }, {
    title: 'Projected ending cash — Low / Expected / High',
    grouping: 'stacked',
    legend: 'b',
    catTitle: 'Forecast month',
    valTitle: `${input.currencySymbol ?? '$'} ending cash`,
    series: [
      { name: 'Low', type: 'area', noFill: true, categoriesRef: cats, valuesRef: sheet.ref(firstDataRow, 1, lastDataRow, 1) },
      { name: 'Low–Expected', type: 'area', color: CFO_PALETTE.brand, alpha: 30, categoriesRef: cats, valuesRef: sheet.ref(firstDataRow, 4, lastDataRow, 4) },
      { name: 'Expected–High', type: 'area', color: CFO_PALETTE.brand, alpha: 16, categoriesRef: cats, valuesRef: sheet.ref(firstDataRow, 5, lastDataRow, 5) },
      { name: 'Expected', type: 'line', color: CFO_PALETTE.brand, smooth: true, categoriesRef: cats, valuesRef: sheet.ref(firstDataRow, 2, lastDataRow, 2) },
    ],
  })

  sheet.freeze(firstDataRow, 1)
  sheet.colWidth(0, 16)
  sheet.colWidths(1, [14, 14, 14, 14, 14])
  return sheet
}

// ---------------------------------------------------------------------------
// 13-Week Cash (weekly liquidity)
// ---------------------------------------------------------------------------

function weekEndingLabel(period: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(period ?? ''))
  return m ? `${m[2]}/${m[3]}` : String(period ?? '')
}

function buildWeeklyCashSheet(
  styles: CfoWorkbookStyleRegistry,
  input: CfoForecastWorkbookInput,
  weekly: CfoStatementData,
): SheetWriter {
  const sheet = new SheetWriter(SHEET.weekly.id, SHEET.weekly.name)
  // Window: ~1 actual week for context + up to 13 forecast weeks.
  const firstFc = Math.max(0, weekly.periodKinds.indexOf('forecast'))
  const start = Math.max(0, firstFc - 1)
  const periods = weekly.periods.slice(start, start + 14)
  const kinds = weekly.periodKinds.slice(start, start + 14)
  const lastCol = FIRST_PERIOD_COL + periods.length - 1

  cfoSheetHeader(sheet, styles, input, {
    title: '13-Week Cash',
    subtitle: 'Weekly direct-cash liquidity, allocated from the monthly forecast. Flip the scenario on the Drivers sheet.',
    lastCol: Math.max(lastCol, 4),
  })

  const headerRow = 5
  const kindRow = 6
  const firstBodyRow = 7
  sheet.text(headerRow, 0, 'Cash line', styles.id('columnHeader'))
  periods.forEach((p, i) => {
    const col = FIRST_PERIOD_COL + i
    const fc = kinds[i] === 'forecast'
    sheet.text(headerRow, col, `WE ${weekEndingLabel(p)}`, styles.custom({
      bold: true, fontSize: 9, align: 'right', verticalAlign: 'middle',
      textColor: fc ? CFO_PALETTE.forecastText : CFO_PALETTE.headerText,
      backgroundColor: fc ? CFO_PALETTE.forecastFill : CFO_PALETTE.headerFill,
      border: { bottom: { style: 'thin', color: CFO_PALETTE.border } },
    }))
    sheet.text(kindRow, col, fc ? 'Forecast' : 'Actual', fc ? styles.id('caseLow') : styles.id('caseExpected'))
  })

  let row = firstBodyRow
  let endingRowIdx = -1
  let netRowIdx = -1
  for (const r of weekly.rows) {
    const isGrand = r.isTotal && r.level <= 0
    const isSub = r.isTotal || r.rowType === 'group'
    const labelStyle = isGrand ? styles.id('total') : isSub ? styles.id('subtotal') : (r.level > 0 ? styles.id('rowLabelIndent') : styles.id('rowLabel'))
    sheet.text(row, 0, `${'  '.repeat(Math.max(0, Math.min(4, r.level)))}${r.label}`, labelStyle)
    periods.forEach((_p, i) => {
      const gi = start + i
      const col = FIRST_PERIOD_COL + i
      const hi = num(r.byCase.high[gi])
      const ex = num(r.byCase.expected[gi])
      const lo = num(r.byCase.low[gi])
      if (hi == null && ex == null && lo == null) return
      const fc = kinds[i] === 'forecast'
      const numberStyle = isGrand ? styles.id('totalNumber') : isSub ? styles.id('subtotalNumber') : fc ? styles.id('forecastNumber') : styles.id('actualNumber')
      sheet.formula(row, col, caseChooseFormula(hi ?? 0, ex ?? 0, lo ?? 0), ex ?? 0, numberStyle)
    })
    if (/ending|closing/i.test(r.label) && /cash/i.test(r.label)) endingRowIdx = row
    if (/net.*(change|cash|movement|flow)/i.test(r.label)) netRowIdx = row
    row += 1
  }

  sheet.freeze(firstBodyRow, FIRST_PERIOD_COL)
  sheet.colWidth(0, 34)
  // Wide enough for 7-figure cash balances (otherwise Excel renders ########).
  for (let c = FIRST_PERIOD_COL; c <= lastCol; c += 1) sheet.colWidth(c, 12)

  if (endingRowIdx >= 0) {
    const R = endingRowIdx + 1
    const first = colLetterLocal(FIRST_PERIOD_COL)
    const last = colLetterLocal(lastCol)
    sheet.addConditionalFormat({
      id: 'wk_min_cash',
      kind: 'expression',
      ranges: [sheet.range(endingRowIdx, FIRST_PERIOD_COL, endingRowIdx, lastCol)],
      priority: 1,
      formula1: `AND(${first}$${R}<>0,${first}$${R}=MIN($${first}$${R}:$${last}$${R}))`,
      format: { backgroundColor: CFO_PALETTE.dangerFill, textColor: CFO_PALETTE.dangerText, bold: true },
    })
    const cats = sheet.ref(headerRow, FIRST_PERIOD_COL, headerRow, lastCol)
    const series: Parameters<SheetWriter['chart']>[2]['series'] = []
    if (netRowIdx >= 0) series.push({ name: 'Net change', type: 'col', color: CFO_PALETTE.brand, categoriesRef: cats, valuesRef: sheet.ref(netRowIdx, FIRST_PERIOD_COL, netRowIdx, lastCol) })
    series.push({ name: 'Closing cash', type: 'line', color: CFO_PALETTE.inputBlue, secondaryAxis: series.length > 0, smooth: true, categoriesRef: cats, valuesRef: sheet.ref(endingRowIdx, FIRST_PERIOD_COL, endingRowIdx, lastCol) })
    sheet.chart('chart_weekly_cash', { r1: row + 1, c1: 0, r2: row + 18, c2: Math.max(8, lastCol) }, {
      title: '13-week cash bridge',
      legend: 'b',
      catTitle: 'Week ending',
      valTitle: 'Net change',
      valTitle2: netRowIdx >= 0 ? 'Closing cash' : undefined,
      series,
    })
  }
  return sheet
}

// ---------------------------------------------------------------------------
// Daily Cash Ledger (windowed daily liquidity)
// ---------------------------------------------------------------------------

function dayLabel(period: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(period ?? ''))
  if (!m) return String(period ?? '')
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  return `${months[Math.max(0, Math.min(11, Number(m[2]) - 1))]} ${m[3]}`
}

function buildDailyLedgerSheet(
  styles: CfoWorkbookStyleRegistry,
  input: CfoForecastWorkbookInput,
  daily: CfoStatementData,
): SheetWriter {
  const sheet = new SheetWriter(SHEET.daily.id, SHEET.daily.name)
  // Window: a couple of actual days for context + ~30 forecast days.
  const firstFc = Math.max(0, daily.periodKinds.indexOf('forecast'))
  const start = Math.max(0, firstFc - 2)
  const periods = daily.periods.slice(start, start + 32)
  const kinds = daily.periodKinds.slice(start, start + 32)
  const lastCol = FIRST_PERIOD_COL + periods.length - 1

  cfoSheetHeader(sheet, styles, input, {
    title: 'Daily Cash Ledger',
    subtitle: 'First ~30 days of daily liquidity, allocated from the monthly forecast. Min-cash days are flagged red.',
    lastCol: Math.max(lastCol, 4),
  })

  const headerRow = 5
  const kindRow = 6
  const firstBodyRow = 7
  sheet.text(headerRow, 0, 'Cash line', styles.id('columnHeader'))
  periods.forEach((p, i) => {
    const col = FIRST_PERIOD_COL + i
    const fc = kinds[i] === 'forecast'
    sheet.text(headerRow, col, dayLabel(p), styles.custom({
      bold: true, fontSize: 8, align: 'right', verticalAlign: 'middle', textRotation: 90,
      textColor: fc ? CFO_PALETTE.forecastText : CFO_PALETTE.headerText,
      backgroundColor: fc ? CFO_PALETTE.forecastFill : CFO_PALETTE.headerFill,
      border: { bottom: { style: 'thin', color: CFO_PALETTE.border } },
    }))
    sheet.text(kindRow, col, fc ? 'F' : 'A', fc ? styles.id('caseLow') : styles.id('caseExpected'))
  })

  let row = firstBodyRow
  let endingRowIdx = -1
  let netRowIdx = -1
  for (const r of daily.rows) {
    const isGrand = r.isTotal && r.level <= 0
    const isSub = r.isTotal || r.rowType === 'group'
    const labelStyle = isGrand ? styles.id('total') : isSub ? styles.id('subtotal') : (r.level > 0 ? styles.id('rowLabelIndent') : styles.id('rowLabel'))
    sheet.text(row, 0, `${'  '.repeat(Math.max(0, Math.min(4, r.level)))}${r.label}`, labelStyle)
    periods.forEach((_p, i) => {
      const gi = start + i
      const col = FIRST_PERIOD_COL + i
      const hi = num(r.byCase.high[gi])
      const ex = num(r.byCase.expected[gi])
      const lo = num(r.byCase.low[gi])
      if (hi == null && ex == null && lo == null) return
      const fc = kinds[i] === 'forecast'
      const numberStyle = isGrand ? styles.id('totalNumber') : isSub ? styles.id('subtotalNumber') : fc ? styles.id('forecastNumber') : styles.id('actualNumber')
      sheet.formula(row, col, caseChooseFormula(hi ?? 0, ex ?? 0, lo ?? 0), ex ?? 0, numberStyle)
    })
    if (/ending|closing|running/i.test(r.label) && /cash|balance/i.test(r.label)) endingRowIdx = row
    if (/net.*(change|cash|movement|flow)/i.test(r.label)) netRowIdx = row
    row += 1
  }

  sheet.freeze(firstBodyRow, FIRST_PERIOD_COL)
  sheet.colWidth(0, 32)
  // Wide enough for 7-figure running balances (otherwise Excel renders ########).
  for (let c = FIRST_PERIOD_COL; c <= lastCol; c += 1) sheet.colWidth(c, 11)

  if (endingRowIdx >= 0) {
    const R = endingRowIdx + 1
    const first = colLetterLocal(FIRST_PERIOD_COL)
    const last = colLetterLocal(lastCol)
    sheet.addConditionalFormat({
      id: 'daily_min_cash',
      kind: 'expression',
      ranges: [sheet.range(endingRowIdx, FIRST_PERIOD_COL, endingRowIdx, lastCol)],
      priority: 1,
      formula1: `AND(${first}$${R}<>0,${first}$${R}=MIN($${first}$${R}:$${last}$${R}))`,
      format: { backgroundColor: CFO_PALETTE.dangerFill, textColor: CFO_PALETTE.dangerText, bold: true },
    })
    sheet.addConditionalFormat({
      id: 'daily_balance_bar',
      kind: 'dataBar',
      ranges: [sheet.range(endingRowIdx, FIRST_PERIOD_COL, endingRowIdx, lastCol)],
      priority: 4,
      dataBar: { color: CFO_PALETTE.brand, min: { type: 'min' }, max: { type: 'max' } },
    })
    const cats = sheet.ref(headerRow, FIRST_PERIOD_COL, headerRow, lastCol)
    const series: Parameters<SheetWriter['chart']>[2]['series'] = []
    if (netRowIdx >= 0) series.push({ name: 'Net daily', type: 'col', color: CFO_PALETTE.brand, categoriesRef: cats, valuesRef: sheet.ref(netRowIdx, FIRST_PERIOD_COL, netRowIdx, lastCol) })
    series.push({ name: 'Running cash', type: 'line', color: CFO_PALETTE.inputBlue, secondaryAxis: series.length > 0, smooth: true, categoriesRef: cats, valuesRef: sheet.ref(endingRowIdx, FIRST_PERIOD_COL, endingRowIdx, lastCol) })
    sheet.chart('chart_daily_cash', { r1: row + 1, c1: 0, r2: row + 18, c2: Math.max(10, lastCol) }, {
      title: 'Daily cash position',
      legend: 'b',
      catTitle: 'Day',
      valTitle: 'Net daily',
      valTitle2: netRowIdx >= 0 ? 'Running cash' : undefined,
      series,
    })
  }
  return sheet
}

// ---------------------------------------------------------------------------
// Accuracy & Confidence — prediction-interval widening
// ---------------------------------------------------------------------------

type CashBands = { labels: string[]; low: number[]; expected: number[]; high: number[] }

/** Cash Low/Expected/High bands — from the ML cash-flow ending row, else the driver model. */
function extractCashBands(input: CfoForecastWorkbookInput): CashBands {
  const cf = input.statements.find((s) => s.statementType === 'cash_flow')
  const endRow = cf?.rows.find((r) => /ending|closing/i.test(r.label) && /cash/i.test(r.label))
  if (cf && endRow) {
    const at = (arr: Array<number | null>, i: number) => (typeof arr[i] === 'number' && Number.isFinite(arr[i] as number) ? (arr[i] as number) : 0)
    // Orient per period: the ML cash ending-balance band can arrive inverted
    // (favourable more-cash outcome labelled `low`); ending cash is "more is better".
    const oriented = cf.periods.map((_p, i) => orientBand(at(endRow.byCase.low, i), at(endRow.byCase.expected, i), at(endRow.byCase.high, i)))
    return {
      labels: cf.periods.map(fmtMonthLabel),
      low: oriented.map((b) => b.low),
      expected: oriented.map((b) => b.expected),
      high: oriented.map((b) => b.high),
    }
  }
  const months = Math.max(1, Math.floor(input.seed.horizonMonths))
  return {
    labels: Array.from({ length: months }, (_v, i) => addMonthsLabel(input.anchorPeriod, i + 1)),
    low: projectDriverModel(input.drivers, input.seed, 'low').endingCash,
    expected: projectDriverModel(input.drivers, input.seed, 'expected').endingCash,
    high: projectDriverModel(input.drivers, input.seed, 'high').endingCash,
  }
}

function buildAccuracySheet(
  styles: CfoWorkbookStyleRegistry,
  input: CfoForecastWorkbookInput,
): SheetWriter {
  const sheet = new SheetWriter(SHEET.accuracy.id, SHEET.accuracy.name)
  const bands = extractCashBands(input)

  cfoSheetHeader(sheet, styles, input, {
    title: 'How confident is this forecast?',
    subtitle: 'Each month shows the Expected cash balance and how far it could reasonably swing either side. Tighter range = more confidence; the range widens further out.',
    lastCol: 7,
  })

  // Headline confidence read: near-term (first 3) vs long-term (last 3) ± range.
  const pctOf = (i: number): number => {
    const exp = bands.expected[i] ?? 0
    return exp !== 0 ? ((bands.high[i] ?? 0) - (bands.low[i] ?? 0)) / 2 / Math.abs(exp) : 0
  }
  const avg = (xs: number[]) => (xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : 0)
  const n = bands.labels.length
  const nearPct = avg(bands.labels.slice(0, Math.min(3, n)).map((_l, i) => pctOf(i)))
  const farPct = avg(bands.labels.slice(Math.max(0, n - 3)).map((_l, i) => pctOf(Math.max(0, n - 3) + i)))
  const tileRow = 5
  const headlineTile = (col: number, label: string, value: string, sub: string) => {
    sheet.text(tileRow, col, label, styles.id('kpiLabel')); sheet.merge(tileRow, col, tileRow, col + 1)
    sheet.text(tileRow + 1, col, value, styles.custom({ bold: true, fontSize: 22, textColor: CFO_PALETTE.ink, align: 'left', verticalAlign: 'middle' })); sheet.merge(tileRow + 1, col, tileRow + 1, col + 1)
    sheet.text(tileRow + 2, col, sub, styles.id('kpiUnit')); sheet.merge(tileRow + 2, col, tileRow + 2, col + 1)
  }
  const pctLabel = (p: number) => `± ${(p * 100).toFixed(p < 0.1 ? 1 : 0)}%`
  headlineTile(0, 'NEAR-TERM RANGE', pctLabel(nearPct), 'first 3 months · highest confidence')
  headlineTile(2, 'LONG-TERM RANGE', pctLabel(farPct), 'final 3 months · widest uncertainty')

  const headerRow = 9
  const firstDataRow = 8
  const headers = ['Forecast month', 'Low', 'Expected', 'High', '± range', 'Confidence']
  headers.forEach((h, i) => sheet.text(headerRow, i, h, i === 0 || i === 5 ? styles.id('columnHeader') : styles.id('columnHeaderRight')))
  const moneyStyle = styles.custom({ fontSize: 10, textColor: CFO_PALETTE.ink, align: 'right', numberFormat: { formatCode: styles.numberFormats.currency } })
  const pctStyle = styles.id('formulaPercent')
  const confStyle = styles.custom({ fontSize: 10, bold: true, textColor: CFO_PALETTE.subtle, align: 'center', verticalAlign: 'middle' })
  bands.labels.forEach((label, i) => {
    const r = firstDataRow + i
    sheet.text(r, 0, label, styles.id('rowLabel'))
    sheet.number(r, 1, bands.low[i] ?? 0, moneyStyle)
    sheet.number(r, 2, bands.expected[i] ?? 0, moneyStyle)
    sheet.number(r, 3, bands.high[i] ?? 0, moneyStyle)
    // ± range as a share of expected: half the Low–High span over |Expected|.
    const half = pctOf(i)
    sheet.formula(r, 4, `IF(${cellLocal(r, 2)}<>0,(${cellLocal(r, 3)}-${cellLocal(r, 1)})/2/ABS(${cellLocal(r, 2)}),0)`, half, pctStyle)
    // Plain-language confidence keyed off the ± range (tighter = higher).
    const conf = half <= 0.1 ? 'High' : half <= 0.2 ? 'Medium' : 'Lower'
    sheet.formula(r, 5, `IF(${cellLocal(r, 4)}<=0.1,"High",IF(${cellLocal(r, 4)}<=0.2,"Medium","Lower"))`, conf, confStyle)
  })
  const lastDataRow = firstDataRow + bands.labels.length - 1

  // Intuitive colour scale on the ± range: green (tight/confident) → amber → red (wide/uncertain).
  sheet.addConditionalFormat({
    id: 'acc_range_scale',
    kind: 'colorScale',
    ranges: [sheet.range(firstDataRow, 4, lastDataRow, 4)],
    priority: 1,
    colorScale: {
      stops: [
        { cfvo: { type: 'min' }, color: CFO_PALETTE.okFill },
        { cfvo: { type: 'percentile', val: 50 }, color: CFO_PALETTE.warnFill },
        { cfvo: { type: 'max' }, color: CFO_PALETTE.dangerFill },
      ],
    },
  })

  // Fan chart: Expected line with the Low–High band, so the widening range is visible
  // as area, not just a percentage.
  const cats = sheet.ref(firstDataRow, 0, lastDataRow, 0)
  sheet.chart('chart_accuracy', { r1: tileRow, c1: 6, r2: tileRow + 18, c2: 16 }, {
    title: 'Expected cash with its prediction range',
    grouping: 'standard',
    legend: 'b',
    catTitle: 'Forecast month',
    valTitle: `${input.currencySymbol ?? '$'} cash`,
    series: [
      { name: 'Low', type: 'line', color: CFO_PALETTE.faint, categoriesRef: cats, valuesRef: sheet.ref(firstDataRow, 1, lastDataRow, 1) },
      { name: 'Expected', type: 'line', color: CFO_PALETTE.brand, smooth: true, categoriesRef: cats, valuesRef: sheet.ref(firstDataRow, 2, lastDataRow, 2) },
      { name: 'High', type: 'line', color: CFO_PALETTE.faint, categoriesRef: cats, valuesRef: sheet.ref(firstDataRow, 3, lastDataRow, 3) },
    ],
  })

  // Honesty note: this is estimated forward uncertainty, not a historical score.
  const noteRow = lastDataRow + 2
  sheet.text(noteRow, 0, 'This shows the model’s estimated forward uncertainty (how wide the range is), not a historical accuracy score. See the Methodology sheet for how the bands are produced.', styles.id('legend'))
  sheet.merge(noteRow, 0, noteRow, 5)
  sheet.rowHeight(noteRow, 28)

  // -------- Historical accuracy (backtest) --------
  // The bands above are forward *estimates*; this block reports how the forecast
  // actually scored against closed actuals — WAPE (average error) and how often
  // the actual landed inside the Low–High range. Three honest states: a real read,
  // a suppressed caveat (stale/incomplete books), or the cold-start message.
  const btRow = noteRow + 2
  sheet.text(btRow, 0, 'Historical accuracy (backtest)', styles.id('sectionHeader'))
  sheet.merge(btRow, 0, btRow, 5)
  const summary = input.backtest
    ? forecastAccuracySummary(input.backtest.rows, {
      recentMonths: input.backtest.recentMonths ?? 3,
      readinessStatus: input.backtest.readinessStatus ?? null,
      basisLabels: input.backtest.basisLabels ?? null,
    })
    : null
  const btNote = (text: string) => {
    sheet.text(btRow + 1, 0, text, styles.id('legend'))
    sheet.merge(btRow + 1, 0, btRow + 1, 5)
    sheet.rowHeight(btRow + 1, 28)
  }
  if (summary && summary.suppressedReason) {
    btNote(summary.suppressedReason === 'books_stale'
      ? 'Your QuickBooks books are stale for the scored window, so a clean accuracy read isn’t available yet. Reconnect or close the latest month and this fills in automatically.'
      : 'The most recent months aren’t fully closed yet, so we’re holding the accuracy score rather than overstate it. It appears once those months close.')
  } else if (summary && summary.byStatement.length) {
    btNote(summary.plainLanguage ?? '')
    const thRow = btRow + 3
    const ths = ['Statement', 'WAPE (avg error)', 'Bias (over / under)', 'Months scored']
    ths.forEach((h, i) => sheet.text(thRow, i, h, i === 0 ? styles.id('columnHeader') : styles.id('columnHeaderRight')))
    const wapeCell = styles.custom({ fontSize: 10, textColor: CFO_PALETTE.ink, align: 'right', numberFormat: { formatCode: '0.0%' } })
    const biasCell = styles.custom({ fontSize: 10, textColor: CFO_PALETTE.subtle, align: 'right', numberFormat: { formatCode: '+0.0%;(0.0%);"–"' } })
    const intCell = styles.custom({ fontSize: 10, textColor: CFO_PALETTE.ink, align: 'right', numberFormat: { formatCode: '0' } })
    summary.byStatement.forEach((s, i) => {
      const r = thRow + 1 + i
      const label = forecastAccuracyStatementLabel(s.statementType)
      sheet.text(r, 0, label.charAt(0).toUpperCase() + label.slice(1), styles.id('rowLabel'))
      sheet.number(r, 1, s.wape ?? s.medianAbsVariancePct, wapeCell)
      if (s.bias != null) sheet.number(r, 2, s.bias, biasCell)
      else sheet.text(r, 2, '—', styles.id('note'))
      sheet.number(r, 3, s.monthCount, intCell)
    })
    if (summary.intervalHitRate != null) {
      const covRow = thRow + 1 + summary.byStatement.length
      sheet.text(covRow, 0, `Range coverage: the actual landed inside the Low–High band ${Math.round(summary.intervalHitRate * 100)}% of the time.`, styles.id('legend'))
      sheet.merge(covRow, 0, covRow, 5)
    }
  } else {
    btNote('Historical accuracy appears here once this forecast has been live across at least one closed month. (Backtesting compares each past forecast to what actually happened.)')
  }

  sheet.freeze(firstDataRow, 1)
  sheet.colWidth(0, 18)
  sheet.colWidths(1, [16, 16, 16, 12, 12])
  return sheet
}

// ---------------------------------------------------------------------------
// Methodology + disclosures (Phase 0 truth layer)
// ---------------------------------------------------------------------------

function buildMethodologySheet(
  styles: CfoWorkbookStyleRegistry,
  input: CfoForecastWorkbookInput,
): SheetWriter {
  const sheet = new SheetWriter(SHEET.methodology.id, SHEET.methodology.name)
  cfoSheetHeader(sheet, styles, input, {
    title: 'Methodology, Confidence & Glossary',
    subtitle: 'How the forecast is produced, how to read the workbook, and what the bands mean.',
    lastCol: 5,
  })

  let r = 5
  const section = (title: string) => { sheet.text(r, 0, title, styles.id('sectionHeader')); sheet.merge(r, 0, r, 5); r += 1 }
  const para = (text: string) => { sheet.text(r, 0, text, styles.id('legend')); sheet.merge(r, 0, r, 5); sheet.rowHeight(r, 28); r += 1 }

  section('How this forecast is produced')
  para('Monthly Income Statement, Balance Sheet and Cash Flow are generated by FuseGrid’s machine-learning forecast from your QuickBooks history. The High / Expected / Low columns are statistical prediction bands from a single model — they are NOT independent bull / base / bear scenarios.')
  para('Below monthly grain the model forecasts cash only (13-week and daily liquidity); these are allocated from the monthly forecast. A daily balance sheet is intentionally not presented.')

  section('Drivers sheet — the playable model')
  para('The Drivers sheet is a transparent, simplified FP&A model seeded to the ML expected anchor. Editing a driver explores a neighbourhood around the ML forecast using standard finance formulas (revenue growth, gross margin, DSO/DPO, opex, seasonality). It is a what-if sandbox, not a re-run of the ML.')

  section('Reading the numbers')
  para('Blue = editable input. Ink = in-sheet formula. Petrol = cross-sheet link. Softly tinted columns = forecast periods (vs actuals). The scenario toggle on the Drivers sheet flips every sheet between High, Expected and Low.')

  section('Glossary')
  const glossary: Array<[string, string]> = [
    ['Runway', 'Months of cash remaining at the current burn rate (Cash balance ÷ monthly burn).'],
    ['Burn', 'Average monthly net cash outflow over the last three forecast months.'],
    ['DSO / DPO', 'Days sales outstanding / days payable outstanding — receivable / payable timing.'],
    ['EBITDA', 'Earnings before interest, tax, depreciation and amortisation.'],
    ['Min cash', 'The lowest projected end-of-period cash balance over the horizon.'],
  ]
  glossary.forEach(([term, def]) => {
    sheet.text(r, 0, term, styles.id('rowLabel'))
    sheet.text(r, 1, def, styles.id('legend'))
    sheet.merge(r, 1, r, 5)
    r += 1
  })

  if (input.warnings && input.warnings.length > 0) {
    r += 1
    section('Notes on this export')
    input.warnings.forEach((w) => para(`•  ${w}`))
  }

  // Data readiness — only surfaced when the package reports a status below "ready",
  // so we never manufacture a warning from missing metadata (the headless path).
  const readiness = input.vintage?.readinessStatus ?? null
  if (readiness && readiness.toLowerCase() !== 'ready') {
    r += 1
    section('Data readiness')
    para(`This forecast is marked "${readiness}". Accuracy claims are suppressed and the forecast is labelled provisional until the underlying QuickBooks books are fully closed and current. As more months close, the model re-grounds automatically and this caveat clears.`)
  }

  r += 1
  section('Provenance')
  const vintageBits: string[] = [
    `Company: ${input.companyName}`,
    `Anchor: ${input.anchorPeriod ?? '—'}`,
  ]
  if (input.vintage?.dataAsOf) vintageBits.push(`Data as of: ${String(input.vintage.dataAsOf).slice(0, 10)}`)
  if (input.vintage?.closedMonthsCount != null) vintageBits.push(`History: ${input.vintage.closedMonthsCount} closed month${input.vintage.closedMonthsCount === 1 ? '' : 's'}`)
  vintageBits.push(`Grain: ${input.grainLabel}`)
  vintageBits.push(`Generated: ${(input.generatedAtIso ?? '').slice(0, 19) || '—'}`)
  if (input.vintage?.packageId) vintageBits.push(`Model: ${input.vintage.packageId}`)
  vintageBits.push(`FuseGrid CFO Core ${input.version ?? 'v1.0'}`)
  para(vintageBits.join(' · ') + '.')

  sheet.colWidth(0, 26)
  sheet.colWidths(1, [40, 24, 24, 24, 24])
  return sheet
}

// ---------------------------------------------------------------------------
// Top-level composer
// ---------------------------------------------------------------------------

export function buildCfoForecastWorkbookSnapshot(input: CfoForecastWorkbookInput): CfoForecastWorkbookResult {
  const styles = buildCfoWorkbookStyleRegistry(input.currencySymbol ?? '$')
  const wb = new WorkbookBuilder({
    workbookId: `cfo_forecast_${(input.anchorPeriod ?? 'wb').replace(/[^0-9a-z]/gi, '')}`,
    title: `${input.companyName} Cash-Flow Forecast`,
    company: input.companyName,
    created: input.generatedAtIso,
    styles: styles.styles,
  })

  const cover = wb.addSheet(buildCoverSheet(wb, styles, input))
  wb.addSheet(buildDriversSheet(wb, styles, input))

  const byType = new Map(input.statements.map((s) => [s.statementType, s]))
  for (const type of ['profit_and_loss', 'balance_sheet', 'cash_flow'] as CfoStatementType[]) {
    const statement = byType.get(type)
    if (statement) wb.addSheet(buildStatementSheet(wb, styles, statement, input))
  }

  if (input.weeklyCash && input.weeklyCash.rows.length > 0) {
    wb.addSheet(buildWeeklyCashSheet(styles, input, input.weeklyCash))
  }
  if (input.dailyCash && input.dailyCash.rows.length > 0) {
    wb.addSheet(buildDailyLedgerSheet(styles, input, input.dailyCash))
  }

  wb.addSheet(buildScenarioSheet(wb, styles, input))
  wb.addSheet(buildForecastRangeSheet(styles, input))
  wb.addSheet(buildAccuracySheet(styles, input))
  wb.addSheet(buildMethodologySheet(styles, input))
  wb.setActiveSheet(cover)

  // The WorkbookBuilder snapshots the style dict at construction time, but the
  // sheet builders above register ad-hoc `styles.custom(...)` styles *after*
  // construction. Re-sync the full registry so those custom styles (currency /
  // percent number formats on the Forecast Range, Scenario and Accuracy sheets)
  // actually reach the snapshot — otherwise those cells reference a missing
  // style id and Excel renders them as unformatted raw floats.
  wb.mergeStyles(styles.styles)

  return { snapshot: wb.build() }
}

// Re-exports for downstream consumers
export { crossRef, crossRangeRef }
